import {
    isPhoneNumberE164,
    ReferenceBusinessId,
    RegionId,
    TaskTypeId,
    toPhoneNumberE164,
} from "@sp-crm/core";
import { useQuery } from "@tanstack/react-query";
import { MessageCompose } from "components/messages/message-compose";
import { trpc } from "components/rpc/trpc";
import { VisitParent } from "components/visits/types";
import { AdvancedSearchEntityType } from "generated/graphql";
import React, { useState } from "react";
import { useFeature, useRegionId } from "store/selectors/hooks";
import { usePhoneLines } from "store/selectors/telecom";
import { handleEvent } from "util/user-events";
import { EntityMarketingPlan } from "./entity-marketing-plan";
import { normalizePendingVisit } from "./marketing-plan-format";
import { OrgCallOptions } from "./touchpoint-row";

interface ReferenceBusinessMarketingPlanProps {
    businessId: ReferenceBusinessId;
    variant: "marketingPlan" | "recommendedActions";
}

export const ReferenceBusinessMarketingPlan: React.FC<
    ReferenceBusinessMarketingPlanProps
> = ({ businessId, variant }) => {
    const marketingPlaybookEnabled = useFeature("marketingPlaybook");
    const regionId = useRegionId();
    const trpcClient = trpc.useTRPC();
    const phoneLines = usePhoneLines();
    const [composingActivityType, setComposingActivityType] = useState<{
        activityTypeId: TaskTypeId | null;
    } | null>(null);

    const planQuery = useQuery({
        ...trpcClient.marketingPlaybook.orgPlan.queryOptions({
            regionId: regionId ?? ("" as RegionId),
            businessId,
        }),
        enabled: marketingPlaybookEnabled && !!regionId,
    });

    if (!planQuery.isSuccess) {
        return null;
    }
    const plan = planQuery.data;

    const phone =
        plan?.businessPhone && isPhoneNumberE164(plan.businessPhone)
            ? toPhoneNumberE164(plan.businessPhone)
            : null;

    const parent: VisitParent | null = plan
        ? { kind: "organization", id: businessId, name: plan.businessName, address: null }
        : null;

    const orgCallOptions: OrgCallOptions | undefined = plan
        ? {
              businessId,
              orgPhone: phone,
              orgName: plan.businessName,
              callableContacts: plan.callableContacts,
          }
        : undefined;

    const openCompose = (activityTypeId: TaskTypeId | null) => {
        handleEvent("marketing-plan-send-email");
        setComposingActivityType({ activityTypeId });
    };

    return (
        <>
            <EntityMarketingPlan
                variant={variant}
                emptyStateNoun="organization"
                touchpoints={
                    plan?.touchpoints.map(touchpoint => ({
                        ...touchpoint,
                        lastContactedAt: touchpoint.lastContactedAt ?? null,
                        lastContactedActivity: touchpoint.lastContactedActivity ?? null,
                        nextDueAt: touchpoint.nextDueAt ?? null,
                        pendingVisit: normalizePendingVisit(touchpoint.pendingVisit),
                    })) ?? null
                }
                parent={parent}
                email={plan?.businessEmail ?? null}
                emailOptedOut={false}
                phone={phone}
                hasPhoneLine={phoneLines.length > 0}
                orgCallOptions={orgCallOptions}
                onSendEmail={openCompose}
            />
            {composingActivityType ? (
                <MessageCompose
                    entityId={businessId}
                    entityType={AdvancedSearchEntityType.ReferenceBusiness}
                    activityTypeId={composingActivityType.activityTypeId}
                    onDismiss={() => setComposingActivityType(null)}
                />
            ) : null}
        </>
    );
};
